import React from 'react';
import styles from './UpgradeModal.module.css';
import { useLanguage } from '../Providers/LanguageContext';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountType: 'new' | 'temp' | 'real';
  userEmail?: string | null;
  userId?: string;
  onSignUpClick?: () => void;
}

// Stripe checkout URL (test mode)
// IMPORTANT: Configure the success URL in your Stripe Dashboard:
// - Success URL: https://yourdomain.com/stripe-success
// - Cancel URL: https://yourdomain.com/main
// For webhook integration to update premium status automatically, see:
// https://stripe.com/docs/webhooks
const STRIPE_CHECKOUT_URL = 'https://buy.stripe.com/test_28E4grcxv38D3BO6w014401';

function UpgradeModal({ isOpen, onClose, accountType, userEmail, userId, onSignUpClick }: UpgradeModalProps) {
  const { t } = useLanguage();

  if (!isOpen) return null;

  const handleUpgradeClick = () => {
    if (accountType === 'real') {
      // Build Stripe URL with prefilled email and client reference ID
      let stripeUrl = STRIPE_CHECKOUT_URL;
      const params = new URLSearchParams();

      // Prefill email if available
      if (userEmail) {
        params.append('prefilled_email', userEmail);
      }

      // Add client reference ID (your user ID) for webhook identification
      if (userId) {
        params.append('client_reference_id', userId);
      }

      // Append parameters to URL if any exist
      if (params.toString()) {
        stripeUrl = `${stripeUrl}?${params.toString()}`;
      }


      // Redirect to Stripe checkout
      window.location.href = stripeUrl;
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeButton} onClick={onClose}>
          &times;
        </button>

        <h2 className={styles.title}>{t('upgrade.title')}</h2>

        <div className={styles.content}>
          {accountType === 'temp' ? (
            <>
              <p className={styles.warningMessage}>
                {t('upgrade.accountRequired')}
              </p>
              <p className={styles.subtitle}>
                {t('upgrade.createAccountPrompt')}
              </p>
            </>
          ) : (
            <p className={styles.subtitle}>{t('upgrade.subtitle')}</p>
          )}

          <div className={styles.starDivider}>✦</div>

          <div className={styles.featuresList}>
            <div className={styles.featureSection}>
              <h3 className={styles.featureTitle}>{t('upgrade.features.history')}</h3>
              <ul className={styles.featureList}>
                <li>{t('upgrade.features.historyItem1')}</li>
                <li>{t('upgrade.features.historyItem2')}</li>
                <li>{t('upgrade.features.historyItem3')}</li>
              </ul>
            </div>

            <div className={styles.featureSection}>
              <h3 className={styles.featureTitle}>{t('upgrade.features.analytics')}</h3>
              <ul className={styles.featureList}>
                <li>{t('upgrade.features.analyticsItem1')}</li>
                <li>{t('upgrade.features.analyticsItem2')}</li>
                <li>{t('upgrade.features.analyticsItem3')}</li>
              </ul>
            </div>

            <div className={styles.featureSection}>
              <h3 className={styles.featureTitle}>{t('upgrade.features.course')}</h3>
              <ul className={styles.featureList}>
                <li>{t('upgrade.features.courseItem1')}</li>
                <li>{t('upgrade.features.courseItem2')}</li>
                <li>{t('upgrade.features.courseItem3')}</li>
              </ul>
            </div>
          </div>

          <div className={styles.buttonContainer}>
            {accountType === 'temp' ? (
              <button
                className={styles.upgradeButton}
                onClick={() => {
                  onClose();
                  onSignUpClick?.();
                }}
              >
                {t('upgrade.createAccount')}
              </button>
            ) : (
              <button
                className={styles.upgradeButton}
                onClick={handleUpgradeClick}
              >
                {t('upgrade.upgradeNow')}
              </button>
            )}
            <button className={styles.cancelButton} onClick={onClose}>
              {t('upgrade.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UpgradeModal;
